import { Box, styled } from '@mui/material';

/**
 * Styling for `EmojiPickerPanel`, the frimousse grid both reaction pickers show inside a DS 2.0
 * Popover. The surface itself (fill, border, radius, padding, shadow) is the Popover's; colours here
 * are DS tokens, so the grid follows whatever scheme the popover inherits.
 */

/** One emoji button (Figma 1912:83205): the grid's cells, and the room picker's quick row. */
export const EMOJI_CELL_SIZE = 40;
/** The grid's inset inside the panel. */
export const EMOJI_PANEL_PADDING = 4;
/** The least air between two cells in a grid row; the columns then spread to fill the width. */
const EMOJI_GRID_GAP = 2;
/** The category header's top padding: the band the grid's top edge fades across. */
const CATEGORY_HEADER_PADDING_TOP = 10;
const MIN_COLUMNS = 6;
const PANEL_MAX_HEIGHT = 320;
/** Space the grid keeps free for the inset scrollbar, so it never covers the right-hand column. */
const SCROLLBAR_GUTTER = 12;

/** The panel's width on a desktop: room for 8 columns, the inset and the scrollbar gutter. */
const EMOJI_PANEL_WIDTH = 360;
/** DS popover padding (8px each side), its border, and a 14px screen gutter. */
const VIEWPORT_GUTTER = 32;

/** The panel width for a given viewport: full size on desktop, viewport minus gutter on phones. */
export const emojiPanelWidth = (viewportWidth: number): number =>
  Math.min(EMOJI_PANEL_WIDTH, viewportWidth - VIEWPORT_GUTTER);

/**
 * Fixed 40px cells; the column count is as many cells, with the least gap between them, as fit the
 * width left after the inset and scrollbar gutter. Leaving the gap out let an iPhone SE's last column
 * run under the scrollbar.
 */
export const emojiPickerColumns = (width: number): number =>
  Math.max(
    MIN_COLUMNS,
    Math.floor(
      (width - EMOJI_PANEL_PADDING * 2 - SCROLLBAR_GUTTER + EMOJI_GRID_GAP) / (EMOJI_CELL_SIZE + EMOJI_GRID_GAP)
    )
  );

/**
 * A 40px emoji button. frimousse marks its keyboard position with `data-active` and keeps focus on
 * the viewport, so grid cells never take focus; a focusable button adds the DS `focusRing()` class.
 */
export const emojiCellStyle = {
  width: EMOJI_CELL_SIZE,
  height: EMOJI_CELL_SIZE,
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'transparent',
  // Round, as the DS 2.0 icon buttons are (design review); the focus ring follows the same circle.
  borderRadius: 'var(--radius-full)',
  color: 'var(--foreground-primary)',
  cursor: 'pointer',
  transition: 'background 80ms ease',
  '&:hover, &[data-active]': {
    background: 'var(--background-control-hover)',
  },
} as const;

export const PanelRoot = styled(Box, { shouldForwardProp: (prop) => prop !== 'width' })<{ width: number }>(
  ({ theme, width }) => ({
    display: 'flex',
    flexDirection: 'column',
    width,
    '[frimousse-root]': {
      display: 'flex',
      flexDirection: 'column',
      height: `min(${PANEL_MAX_HEIGHT}px, 50vh)`,
      width: '100%',
    },
    '[frimousse-viewport]': {
      position: 'relative',
      flex: 1,
      outline: 'none',
      overflowX: 'hidden',
      overflowY: 'auto',
      scrollbarGutter: 'stable',
      padding: `0 ${EMOJI_PANEL_PADDING}px ${EMOJI_PANEL_PADDING}px`,
      // Rows were seen peeking above the pinned category header, under the search (design review).
      // Fading the top edge across the header's own top padding hides anything there and leaves
      // the header's label untouched.
      maskImage: `linear-gradient(to bottom, transparent, #000 ${CATEGORY_HEADER_PADDING_TOP}px)`,
    },
    '[frimousse-loading], [frimousse-empty]': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      color: 'var(--foreground-secondary)',
      fontSize: theme.typography.body2.fontSize,
    },
    '[frimousse-list]': {
      userSelect: 'none',
    },
    '[frimousse-category-header]': {
      padding: `${CATEGORY_HEADER_PADDING_TOP}px 4px 8px`,
      background: 'var(--ds-popover-bg)',
      color: 'var(--foreground-secondary)',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
    },
    // A grid of exactly `--frimousse-list-columns` (frimousse's `columns`) 40px tracks, spread across
    // the row: full rows reach the scrollbar whatever its real width, rather than leaving a strip of
    // empty grid beside it (31px on an iPhone SE), and a part-filled row (a search almost always ends
    // in one) keeps the same tracks from the left instead of floating away from the column above.
    // `EmojiGridRow` drops frimousse's inline `display: flex` so this applies.
    '[frimousse-row]': {
      display: 'grid',
      gridTemplateColumns: `repeat(var(--frimousse-list-columns), ${EMOJI_CELL_SIZE}px)`,
      justifyContent: 'space-between',
    },
    '[frimousse-emoji]': emojiCellStyle,
  })
);

/** The DS Search, as the first row of the panel (`placement="menu"`), with room below it. */
export const SearchRow = styled('div')({
  marginBottom: 4,
});
